import { useCallback, useEffect, useState } from "react";
import { useDemoTrigger, useLiveData } from "./hooks/useLiveData";
import { TopNav } from "./components/shell/TopNav";
import { AlertBanner } from "./components/shell/AlertBanner";
import { AlertDrawer } from "./components/shell/AlertDrawer";
import { RunbookModal } from "./components/shell/RunbookModal";
import { PasswordGate } from "./components/PasswordGate";
import { CheatsheetProvider } from "./cheatsheets/provider";
import { Overview } from "./components/views/Overview";
import { NodeDrilldown } from "./components/views/NodeDrilldown";
import { JobPerformance } from "./components/views/JobPerformance";
import type { Alert } from "./types";

type View = "overview" | "node" | "job";

export function App() {
  return (
    <PasswordGate>
      <CheatsheetProvider>
        <AppInner />
      </CheatsheetProvider>
    </PasswordGate>
  );
}

function AppInner() {
  const { snapshot, status } = useLiveData();
  const demoTrigger = useDemoTrigger();
  const [view, setView] = useState<View>(() => {
    const v = localStorage.getItem("rubin:view");
    return v === "node" || v === "job" ? v : "overview";
  });
  const [timeRange, setTimeRange] = useState("15m");
  const [nodeVar, setNodeVar] = useState<string | null>(null);
  const [jobIdVar, setJobIdVar] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [runbookAlert, setRunbookAlert] = useState<Alert | null>(null);
  const [demoMenuOpen, setDemoMenuOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("rubin:view", view);
  }, [view]);

  // Seed defaults once snapshot arrives
  useEffect(() => {
    if (!snapshot) return;
    if (!nodeVar && snapshot.nodes[0]) setNodeVar(snapshot.nodes[0].name);
    if (!jobIdVar) {
      const running = snapshot.jobs.find((j) => j.state === "RUNNING" && j.gpus.length);
      if (running) setJobIdVar(running.id);
      else if (snapshot.jobs[0]) setJobIdVar(snapshot.jobs[0].id);
    }
  }, [snapshot, nodeVar, jobIdVar]);

  const drillNode = useCallback((n: string) => {
    setNodeVar(n);
    setView("node");
  }, []);

  const drillJob = useCallback((id: number) => {
    setJobIdVar(id);
    setView("job");
  }, []);

  // Keyboard
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setRunbookAlert(null);
        setDrawerOpen(false);
      }
      if (e.key === "1") setView("overview");
      if (e.key === "2") setView("node");
      if (e.key === "3") setView("job");
      if (
        e.key === "a" &&
        !e.metaKey &&
        !e.ctrlKey &&
        (document.activeElement as HTMLElement | null)?.tagName !== "SELECT" &&
        (document.activeElement as HTMLElement | null)?.tagName !== "INPUT"
      ) {
        setDrawerOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (!snapshot) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          color: "var(--fg-2)",
          fontFamily: "var(--mono)",
        }}
      >
        <div>
          <div style={{ color: "var(--cyan)", fontSize: 18, letterSpacing: 2, marginBottom: 8 }}>
            VERA RUBIN HPC
          </div>
          <div>
            {status === "error"
              ? "backend unreachable — check RUBIN_HOST/port and cors"
              : "loading snapshot…"}
          </div>
        </div>
      </div>
    );
  }

  const crumbs =
    view === "node"
      ? `overview / ${nodeVar ?? ""}`
      : view === "job"
      ? `overview / job ${jobIdVar ?? ""}`
      : null;

  return (
    <div className="screen-root">
      <AlertBanner alerts={snapshot.alerts} onOpen={() => setDrawerOpen(true)} />
      <TopNav
        view={view}
        onView={setView}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        alerts={snapshot.alerts}
        onBell={() => setDrawerOpen(true)}
        crumbs={crumbs}
        snapshot={snapshot}
        status={status}
      />

      <div className="screen-main">
        {view === "overview" && (
          <Overview
            snapshot={snapshot}
            onDrillNode={drillNode}
            onDrillJob={drillJob}
            onAlertClick={setRunbookAlert}
          />
        )}
        {view === "node" && nodeVar && (
          <NodeDrilldown
            snapshot={snapshot}
            nodeVar={nodeVar}
            setNodeVar={setNodeVar}
            onDrillJob={drillJob}
          />
        )}
        {view === "job" && jobIdVar && (
          <JobPerformance
            snapshot={snapshot}
            jobIdVar={jobIdVar}
            setJobIdVar={setJobIdVar}
            onDrillNode={drillNode}
          />
        )}
      </div>

      {/* footer */}
      <div
        style={{
          padding: "6px 16px",
          borderTop: "1px solid var(--line)",
          background: "var(--bg-1)",
          display: "flex",
          gap: 14,
          fontSize: 10,
          color: "var(--fg-3)",
          fontFamily: "var(--mono)",
        }}
      >
        <span>
          <span className="kbd">1</span>
          <span className="kbd">2</span>
          <span className="kbd">3</span> views
        </span>
        <span>
          <span className="kbd">A</span> alerts
        </span>
        <span>
          <span className="kbd">?</span> cheatsheet
        </span>
        <span>
          <span className="kbd">Esc</span> close
        </span>
        <span style={{ flex: 1 }} />
        <a
          href="https://nemo-ai-portfolio.pages.dev/#bottom"
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => {
            if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
              e.preventDefault();
              window.open("https://nemo-ai-portfolio.pages.dev/#bottom", "_blank", "noopener,noreferrer");
            }
          }}
          style={{ color: "var(--fg-2)", textDecoration: "none" }}
          title="Open Nadav Chen's portfolio"
        >
          created by <span style={{ color: "var(--cyan)" }}>Nadav Chen</span>
          <span style={{ marginLeft: 4, color: "var(--fg-3)" }}>↗</span>
        </a>
        <span>
          tick <span className="u-dim">{snapshot.tick}</span>
        </span>
        <span>{new Date(snapshot.t).toISOString().slice(0, 19)}Z</span>
        <span>
          stream ·{" "}
          <span
            style={{
              color:
                status === "live"
                  ? "var(--emerald)"
                  : status === "demo"
                  ? "var(--violet)"
                  : status === "polling"
                  ? "var(--amber)"
                  : "var(--rose)",
            }}
          >
            {status}
          </span>
        </span>
        <span>cluster · {snapshot.cluster_name}</span>
      </div>

      <AlertDrawer
        alerts={snapshot.alerts}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSelect={(a) => {
          setRunbookAlert(a);
          setDrawerOpen(false);
        }}
      />
      <RunbookModal alert={runbookAlert} onClose={() => setRunbookAlert(null)} />

      {/* demo triggers */}
      <div
        style={{
          position: "fixed",
          right: 16,
          bottom: 44,
          zIndex: 50,
        }}
      >
        {demoMenuOpen && (
          <div
            className="fade-in"
            style={{
              background: "var(--bg-1)",
              border: "1px solid var(--line-2)",
              borderRadius: 8,
              padding: 8,
              marginBottom: 8,
              minWidth: 240,
              boxShadow: "var(--shadow-2)",
            }}
          >
            <div
              style={{
                fontSize: 9,
                letterSpacing: 1,
                color: "var(--fg-3)",
                padding: "4px 8px",
              }}
            >
              TRIGGER DEMO EVENT
            </div>
            {[
              { label: "Fire XID 79 (critical)", action: "xid79" },
              { label: "Take node down", action: "node-down" },
              { label: "IB link drop", action: "ib-link-down" },
              { label: "IB symbol error spike", action: "ib-symbol-spike" },
              { label: "GPU memory > 90%", action: "gpu-mem-high" },
              { label: "— Resolve all", action: "resolve-all", danger: true },
            ].map((o) => (
              <button
                key={o.action}
                className="btn ghost"
                style={{
                  width: "100%",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  marginTop: 2,
                  color: o.danger ? "var(--emerald)" : undefined,
                }}
                onClick={() => demoTrigger(o.action as Parameters<typeof demoTrigger>[0])}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
        <button
          className="btn primary"
          onClick={() => setDemoMenuOpen((o) => !o)}
          style={{
            height: 36,
            padding: "0 14px",
            boxShadow: "0 8px 32px rgba(78,225,240,0.35)",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M6 1 L10 6 L6 11 L2 6 Z" fill="currentColor" />
          </svg>
          Demo triggers
        </button>
      </div>
    </div>
  );
}
